import React from "react";
import { Lock, ArrowsClockwise } from "@phosphor-icons/react";
import SyncedBanner from "@/components/social/SyncedBanner";

/**
 * StaticStyleSelector — pannello scelta layout (struttura/font) + colore
 * (palette pastello) per i formati STATICI del Social Studio.
 *
 * iter v28 r146 — User M1063: rimossa la sezione AI photo selection (ora
 * vive nella colonna destra accanto al Pacchetto Social). I cerchi colore
 * sono ora compatti single-line (flex wrap) coerenti col profilo agenzia.
 */
export default function StaticStyleSelector({
  isPro,
  userPlan = "free", // iter v28 r185 — 3 tier per colori (free=3, base=5, pro=13)
  layoutOptions,
  colorOptions,
  layoutMini,
  staticLayout,
  setStaticLayout,
  staticColor,
  setStaticColor,
  styleSyncing,
  stylePulse,
}) {
  // iter v29 r136 — Niente più gating sui colori (tutti disponibili).
  const isColorLocked = () => false;
  const colorLabel = `${colorOptions.length} disponibili`;
  return (
    <div className="mt-5 pt-4 border-t border-[#EAE7DF]" data-testid="static-style-selector">
      {/* Layout (struttura/font) */}
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65] mb-2">
        Layout · {isPro ? "4 disponibili" : "1 con Free"}
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-4">
        {layoutOptions.map((l) => {
          const locked = !isPro && l.pro;
          const isActiveLayout = staticLayout === l.key;
          const miniBars = layoutMini[l.key] || [];
          return (
            <button
              key={l.key}
              onClick={() => !locked && setStaticLayout(l.key)}
              disabled={locked}
              className={`relative p-1.5 rounded-lg border text-left transition-all ${
                isActiveLayout ? "border-[#7C8B6E] bg-[#F1F4ED]" :
                locked         ? "border-[#EAE7DF] opacity-55 cursor-not-allowed" :
                                 "border-[#EAE7DF] hover:border-[#7C8B6E]/40 hover:bg-[#FAFAF7]"
              }`}
              data-testid={`static-layout-${l.key}`}
              title={locked ? "Sblocca con il piano Pro" : l.name}
            >
              <div className="w-full h-7 rounded mb-1 bg-white border border-[#EAE7DF] flex flex-col justify-end p-1 gap-[2px] overflow-hidden">
                {miniBars.map((bar, i) => (
                  <div key={i} style={{ height: bar.h, width: bar.w, background: bar.c, alignSelf: bar.a, borderRadius: 1 }} />
                ))}
              </div>
              <div className="text-[10px] font-semibold text-[#1C1C1A] leading-tight flex items-center gap-1">
                {l.name}
                {locked && <Lock size={8} weight="fill" className="text-amber-700" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Colore (palette pastello) — iter v28 r185 ordine cromatico
          (verdi → caldi → rosa/rossi → blu/viola → neutri) + 3 tier
          (free=3, base=5, pro=13). I bloccati restano visibili con un
          lucchetto, così l'agente vede SUBITO che la palette esiste. */}
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65] mb-2">
        Colore · {colorLabel}
      </div>
      <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
        {colorOptions.map((c) => {
          const locked = isColorLocked(c);
          const isActiveColor = staticColor === c.key;
          // iter v28 r239 — User M1155: pallino BIANCO ha bisogno di un
          // ring più scuro per essere visibile sul card bianca della UI.
          const isWhite = (c.swatch || "").toLowerCase() === "#ffffff";
          const ringIdle = isWhite ? "ring-[#9A968B]" : "ring-[#E5E7E0]";
          return (
            <button
              key={c.key}
              onClick={() => !locked && setStaticColor(c.key)}
              disabled={locked}
              style={{
                backgroundColor: c.swatch,
                // iter v29 r068 — User M1421: i pallini comparivano OVALI
                // anziché tondi. Causa: padding di default del <button>
                // dei browser (mobile Safari/Chrome) stiravano l'elemento
                // a ~30×28 px → border-radius:9999px produceva ellisse.
                // Fix: dimensioni esatte 32×32 + padding/border azzerati
                // + box-sizing border-box per cerchio perfetto.
                width: "32px",
                height: "32px",
                minWidth: "32px",
                minHeight: "32px",
                padding: 0,
                borderWidth: 0,
                boxSizing: "border-box",
                borderRadius: "9999px",
              }}
              className={`relative rounded-full transition-all flex-shrink-0 overflow-hidden ${
                isActiveColor ? "ring-2 ring-offset-2 ring-[#1C1C1A] scale-110" :
                locked        ? `opacity-60 cursor-not-allowed ring-1 ${ringIdle}` :
                                `hover:scale-110 ring-1 ${ringIdle}`
              }`}
              data-testid={`static-color-${c.key}`}
              title={locked ? `${c.name} · sblocca con ${c.tier === "base" ? "Start" : "Pro"}` : c.name}
            >
              {locked && (
                <span
                  className="absolute inset-0 flex items-center justify-center bg-black/35"
                  aria-hidden
                >
                  <Lock size={12} weight="fill" className="text-white drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.45)]" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-md bg-[#F1F4ED]/70 border border-[#D4DCC8]/60"
        data-testid="style-applies-everywhere"
      >
        <ArrowsClockwise
          size={12}
          weight="bold"
          className="text-[#7C8B6E] mt-0.5 shrink-0"
        />
        <p className="text-[11px] text-[#1C1C1A] leading-snug">
          <span className="font-semibold">Lo stile si applica a tutti i formati</span>
          <span className="text-[#6B6A65]">
            {" "}— post · carosello · reel · story · <strong className="text-[#1C1C1A]">PDF stampabile A4</strong>. Identità coerente ovunque.
          </span>
        </p>
      </div>

      <SyncedBanner syncing={styleSyncing} pulseKey={stylePulse} />

      {/* iter v29 r136 — Tolto disclaimer "Free: 3 · Base: 5 · Pro: 15" perché
          i colori sono ora tutti sempre disponibili (allineati al brand profilo). */}
      {/* iter v28 r146 — AI photo selection spostato nella colonna DESTRA
          (vicino al Pacchetto Social). Sezione rimossa da qui. */}
    </div>
  );
}
